import { sortBy } from 'lodash';
import React from 'react';
import { useIntl } from 'react-intl';

import { getHandlersKeyFromSearchResult } from '~/components/SearchV2/SearchResult/SearchResult.utils';
import { trackSegment } from '~/components/SegmentAnalytics';
import { Box } from '~/eds/src/components/Box/Box';
import { Image } from '~/eds/src/components/Image/Image';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Panel_DEPRECATED } from '~/eds/src/components/Panel/Panel_DEPRECATED';
import { Text } from '~/eds/src/components/Text/Text';
import { Action } from '~/eds/src/types/Action';
import { useFormatDate } from '~/i18n/hooks/useFormatDate/useFormatDate';
import { useFormatNumber } from '~/i18n/hooks/useFormatNumber/useFormatNumber';
import { api } from '~/redux/api/createApi';
import { DocumentSearchResultItem } from '~/redux/api/methods/search';

import ClauseMatches from './ClauseMatches/ClauseMatches';
import { Fields } from './Fields/Fields';
import SearchMatches from './SearchMatches/SearchMatches';

export interface Props {
  actions?: Action<unknown>[];
  document?: DocumentSearchResultItem;
  onHide: () => void;
}

function NoMatch({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <Layout align="center" direction="column" spacing={4}>
      <Image alt="empty results" image="empty-no-search" />
      <Text textAlign="center" variant="subtitle">
        {title}
      </Text>
      <Text preset="description" textAlign="center">
        {description}
      </Text>
    </Layout>
  );
}

function DetailsPanel({ actions, document, onHide }: Props) {
  const intl = useIntl();
  const formatDate = useFormatDate();
  const formatNumber = useFormatNumber();

  // TODO: team to refactor to specify tab.panel to be compliant with a11y

  if (!document) {
    return null;
  }

  const handlers = getHandlersKeyFromSearchResult(document);
  const title = handlers[0].document_name;
  const fullTextMatches = document.full_text_matches.map((item) => ({
    content: [item],
  }));
  const provisions = sortBy(document.provision_list, 'name'); // for some reason the data isn't sorted
  const hasNoMatch = !fullTextMatches.length;

  api.endpoints.getDocumentInformation.useQuery({
    documentId: document.document_handlers[0].id,
    formatDate,
    formatNumber,
  });

  const tabs = [
    {
      label: intl.formatMessage({
        id: 'DetailsPanel.fieldsTab',
        defaultMessage: 'Fields',
        description: 'Fields tab label',
      }),
      value: 'fields',
      panel: (
        <Fields
          handlerId={document.document_handlers[0].id}
          documentId={document.id}
        />
      ),
    },
    {
      label: intl.formatMessage({
        id: 'DetailsPanel.clausesTab',
        defaultMessage: 'Clauses',
        description: 'Clauses tab label',
      }),
      value: 'clauses',
      panel: (
        <Box pt={4} pr={4} pl={4}>
          {!provisions.length && (
            <NoMatch
              title={intl.formatMessage({
                id: 'DetailsPanel.noKeyClauseTitle',
                defaultMessage: 'No key clause found',
                description: 'Title when no key clauses are found',
              })}
              description={intl.formatMessage({
                id: 'DetailsPanel.noKeyClauseDescription',
                defaultMessage:
                  'Refine your Clause Type filter to show key clause matches',
                description: 'Description when no key clauses are found',
              })}
            />
          )}
          <Layout direction="column" spacing={8}>
            <ClauseMatches clauses={provisions} />
          </Layout>
        </Box>
      ),
    },
    {
      label: intl.formatMessage({
        id: 'DetailsPanel.keywordMatchesTab',
        defaultMessage: 'Keyword Matches',
        description: 'Keyword Matches tab label',
      }),
      value: 'matches',
      panel: (
        <Box pt={4} pr={4} pl={4}>
          {hasNoMatch && (
            <NoMatch
              title={intl.formatMessage({
                id: 'DetailsPanel.noKeywordMatchesTitle',
                defaultMessage: 'No keyword text matches',
                description: 'Title when no keyword matches are found',
              })}
              description={intl.formatMessage({
                id: 'DetailsPanel.noKeywordMatchesDescription',
                defaultMessage:
                  'Refine your search query to show keyword text matches',
                description: 'Description when no keyword matches are found',
              })}
            />
          )}
          <Layout direction="column" spacing={8}>
            <SearchMatches matches={fullTextMatches as any} />
          </Layout>
        </Box>
      ),
    },
  ];

  const handleSelectTab = (tab: string) => {
    trackSegment('selectTab', {
      name: document?.id,
      value: tab,
    });
  };

  return (
    <Panel_DEPRECATED
      isPadded
      title={title}
      actions={actions}
      isVisible={true}
      onHide={onHide}
      tabs={tabs}
      onSelectTab={handleSelectTab}
    />
  );
}

export default DetailsPanel;
